import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, X, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  apiMeta, apiSearchProducts, apiOffersPreview, apiOffersApply, apiOffersList,
  apiOffersEnd, apiGetSettings, apiUpdateSettings, eur, errMsg, GRADE_LABELS,
} from "../adminApi";

const toIso = (local) => (local ? new Date(local).toISOString() : null);
const fmt = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Offers() {
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [scopeType, setScopeType] = useState("category");
  const [scopeKey, setScopeKey] = useState("");
  const [pickedProducts, setPickedProducts] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [discountType, setDiscountType] = useState("percent");
  const [value, setValue] = useState("");
  const [rounding, setRounding] = useState("end90");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const [list, setList] = useState(null);
  const [threshold, setThreshold] = useState(2);

  useEffect(() => {
    apiMeta().then(setMeta).catch(() => {});
    loadList();
    apiGetSettings().then((s) => setThreshold(s.low_stock_threshold)).catch(() => {});
  }, []);

  const loadList = () => apiOffersList().then(setList).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    if (scopeType !== "products" || !q) { setResults([]); return; }
    const t = setTimeout(() => apiSearchProducts(q).then(setResults).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, scopeType]);

  const buildPayload = () => {
    const scope = scopeType === "products"
      ? { type: "products", product_ids: pickedProducts.map((p) => p.id) }
      : { type: scopeType, key: scopeKey };
    return {
      scope, discount_type: discountType, value: Number(value) || 0,
      rounding, start: toIso(start), end: toIso(end),
    };
  };

  const validScope = () =>
    (scopeType === "products" && pickedProducts.length > 0) ||
    (scopeType !== "products" && scopeKey);

  const doPreview = async () => {
    if (!validScope()) { toast.error("Scegli l'ambito dell'offerta."); return; }
    if (!value) { toast.error("Inserisci lo sconto."); return; }
    setBusy(true);
    try { setPreview(await apiOffersPreview(buildPayload())); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const doApply = async () => {
    setBusy(true);
    try {
      const res = await apiOffersApply(buildPayload());
      toast.success(`Offerta applicata a ${res.applied} varianti`);
      setPreview(null); setValue(""); setPickedProducts([]);
      loadList();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const endOffer = async (variant_id) => {
    try { await apiOffersEnd({ variant_ids: [variant_id] }); toast.success("Offerta terminata"); loadList(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveThreshold = async (v) => {
    setThreshold(v);
    try { await apiUpdateSettings({ low_stock_threshold: Number(v) }); toast.success("Soglia salvata"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const OffersTable = ({ rows, showEnd }) => (
    <Table>
      <TableHeader>
        <TableRow className="border-line hover:bg-transparent">
          <TableHead>Prodotto</TableHead><TableHead>Variante</TableHead>
          <TableHead>Prezzo</TableHead><TableHead>Offerta</TableHead>
          <TableHead>Inizio</TableHead><TableHead>Fine</TableHead>
          {showEnd && <TableHead></TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow className="border-line"><TableCell colSpan={showEnd ? 7 : 6} className="py-6 text-center text-sm text-steel">Nessuna offerta.</TableCell></TableRow>
        ) : rows.map((r) => (
          <TableRow key={r.variant_id} className="border-line">
            <TableCell className="text-chalk">{r.product_name}</TableCell>
            <TableCell className="text-steel">{r.memory ? `${r.memory}GB · ` : ""}{GRADE_LABELS[r.grade] || r.grade}</TableCell>
            <TableCell className="text-steel line-through">{eur(r.regular)}</TableCell>
            <TableCell className="font-medium text-lime">{eur(r.sale_price)}</TableCell>
            <TableCell className="text-steel">{fmt(r.start)}</TableCell>
            <TableCell className="text-steel">{fmt(r.end)}</TableCell>
            {showEnd && <TableCell><Button variant="ghost" size="sm" className="text-carrot" onClick={() => endOffer(r.variant_id)}>Termina</Button></TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Offerte</h1>
          <p className="mt-1 text-sm text-steel">Crea sconti e gestisci le offerte attive.</p>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-surface2 px-3 py-2">
          <Label className="text-xs text-steel">Soglia "Ultimi pezzi"</Label>
          <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} onBlur={(e) => saveThreshold(e.target.value)} className="h-8 w-16" data-testid="low-stock-threshold-input" />
        </div>
      </div>

      {/* Creatore offerta in blocco */}
      <Card className="border-line bg-surface2" data-testid="offers-bulk-card">
        <CardHeader><CardTitle className="text-base">Offerta in blocco</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Label>Ambito</Label>
            <div className="flex gap-2">
              <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeKey(""); setPreview(null); }}>
                <SelectTrigger className="w-[150px]" data-testid="offer-scope-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Categoria</SelectItem>
                  <SelectItem value="brand">Marchio</SelectItem>
                  <SelectItem value="products">Prodotti scelti</SelectItem>
                </SelectContent>
              </Select>
              {scopeType === "category" && (
                <Select value={scopeKey} onValueChange={setScopeKey}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                  <SelectContent>{meta.categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {scopeType === "brand" && (
                <Select value={scopeKey} onValueChange={setScopeKey}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Scegli marchio" /></SelectTrigger>
                  <SelectContent>{meta.brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            {scopeType === "products" && (
              <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotti da aggiungere" className="pl-9" />
                </div>
                {results.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-line bg-surface3">
                    {results.map((r) => (
                      <button key={r.id} className="block w-full px-3 py-2 text-left text-sm text-chalk hover:bg-surface"
                        onClick={() => { if (!pickedProducts.find((p) => p.id === r.id)) setPickedProducts([...pickedProducts, r]); setQ(""); setResults([]); }}>
                        {r.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {pickedProducts.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-surface3 px-2 py-1 text-xs text-chalk">
                      {p.name}<button onClick={() => setPickedProducts(pickedProducts.filter((x) => x.id !== p.id))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Tipo sconto</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger data-testid="offer-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">Percentuale %</SelectItem><SelectItem value="amount">Importo €</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Valore</Label>
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="offer-value-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Arrotondamento</Label>
              <Select value={rounding} onValueChange={setRounding}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem><SelectItem value="end00">a ,00</SelectItem><SelectItem value="end90">a ,90</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Inizio (facolt.)</Label>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Fine (facolt.)</Label>
              <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={doPreview} disabled={busy} data-testid="offer-preview-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Anteprima prezzi
            </Button>
            {preview && <Button variant="lime" size="sm" onClick={doApply} disabled={busy} data-testid="offer-apply-button"><Percent className="h-4 w-4" /> Applica a {preview.count} varianti</Button>}
          </div>

          {preview && (
            <div className="md:col-span-2 overflow-x-auto rounded-lg border border-line">
              <Table data-testid="offer-preview-table">
                <TableHeader><TableRow className="border-line hover:bg-transparent"><TableHead>Prodotto</TableHead><TableHead>Variante</TableHead><TableHead>Prima</TableHead><TableHead>Dopo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 50).map((r) => (
                    <TableRow key={r.variant_id} className="border-line">
                      <TableCell className="text-chalk">{r.product_name}</TableCell>
                      <TableCell className="text-steel">{r.memory ? `${r.memory}GB · ` : ""}{GRADE_LABELS[r.grade] || r.grade}</TableCell>
                      <TableCell className="text-steel line-through">{eur(r.old_price)}</TableCell>
                      <TableCell className="font-medium text-lime">{eur(r.new_sale_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista offerte */}
      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="text-base">Offerte</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {!list ? <p className="text-sm text-steel">Caricamento…</p> : (
            <Tabs defaultValue="active">
              <TabsList>
                <TabsTrigger value="active" data-testid="offers-tab-active">Attive ({list.active.length})</TabsTrigger>
                <TabsTrigger value="scheduled">Programmate ({list.scheduled.length})</TabsTrigger>
                <TabsTrigger value="expired">Scadute ({list.expired.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active" className="mt-3"><OffersTable rows={list.active} showEnd /></TabsContent>
              <TabsContent value="scheduled" className="mt-3"><OffersTable rows={list.scheduled} showEnd /></TabsContent>
              <TabsContent value="expired" className="mt-3"><OffersTable rows={list.expired} showEnd={false} /></TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
